<template>
  <section class="pph">
    <h5 v-if="showTitle" class="d-flex align-items-center mb-3" style="gap:.5rem">
      <CIcon name="cil-history" height="22" />{{ $t('product.purchase_price_history') }}
    </h5>
    <div class="pph-stats">
      <article v-for="stat in statistics" :key="stat.key" class="pph-stat">
        <small>{{ stat.label }}</small><strong>{{ money(stat.value) }}</strong>
        <span v-if="unit(stat.value) !== null">{{ $t('packaging.unit_price') }}: {{ money(unit(stat.value)) }}</span>
      </article>
    </div>
    <div class="pph-history-grid mt-4">
      <article class="pph-panel">
        <div class="pph-panel__header">
          <h6>{{ $t('product.purchase_price_change') }}</h6><small>{{ chartCurrency }}</small>
        </div>
        <div v-if="chartPrices.length" class="pph-chart">
          <CChartLine :datasets="chartDatasets" :labels="chartLabels" :options="chartOptions" />
        </div>
        <div v-else class="pph-empty">
          <CIcon name="cil-ban" height="26" />{{ $t('global.no_items_found') }}
        </div>
      </article>
      <article class="pph-panel">
        <div class="pph-panel__header">
          <h6>{{ $t('product.purchase_price_history') }}</h6><small>{{ prices.length }}</small>
        </div>
        <div class="table-responsive pph-table">
          <table class="table table-hover mb-0">
        <thead><tr>
          <th>{{ $t('global.valid_from') }}</th>
          <th class="text-right">{{ $t('product.price_purchase') }}</th>
          <th v-if="hasPackaging" class="text-right">{{ $t('packaging.unit_price') }}</th>
        </tr></thead>
        <tbody>
          <tr v-for="item in prices" :key="item.id || item.created_at">
            <td>{{ date(item.created_at) }}</td>
            <td class="text-right font-weight-bold">{{ money(item.amount, item.currency) }}</td>
            <td v-if="hasPackaging" class="text-right text-muted">{{ money(unit(item.amount), item.currency) }}</td>
          </tr>
          <tr v-if="!prices.length"><td :colspan="hasPackaging ? 3 : 2" class="text-center text-muted py-5">
            <CIcon name="cil-ban" height="26" class="d-block mx-auto mb-2" />{{ $t('global.no_items_found') }}
          </td></tr>
        </tbody>
          </table>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { CChartLine } from "@coreui/vue-chartjs";

export default {
  name: "purchase-price-history",
  components: { CChartLine },
  props: {
    purchase_prices: { type: Array, default: () => [] },
    current_purchase_price_amount: { type: [Number, String], default: null },
    average_purchase_price_amount: { type: [Number, String], default: null },
    weighted_purchase_price_amount: { type: [Number, String], default: null },
    currency: { type: String, default: "EUR" },
    packaged_quantity: { type: [Number, String], default: null },
    showTitle: { type: Boolean, default: true },
  },
  computed: {
    prices() { return [...(this.purchase_prices || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); },
    amounts() { return this.prices.map((p) => this.number(p.amount)).filter((n) => n !== null); },
    average() {
      const supplied = this.number(this.average_purchase_price_amount);
      return supplied !== null ? supplied : (this.amounts.length ? this.amounts.reduce((a, b) => a + b, 0) / this.amounts.length : null);
    },
    statistics() {
      return [
        { key: "current", label: this.$t('product.current_purchase_price'), value: this.current_purchase_price_amount },
        { key: "average", label: this.$t('product.average_purchase_price'), value: this.average },
        { key: "weighted", label: this.$t('product.weighted_average_purchase_price'), value: this.weighted_purchase_price_amount },
        { key: "min", label: this.$t('product.minimum_purchase_price'), value: this.amounts.length ? Math.min(...this.amounts) : null },
        { key: "max", label: this.$t('product.maximum_purchase_price'), value: this.amounts.length ? Math.max(...this.amounts) : null },
      ];
    },
    hasPackaging() { const q = this.number(this.packaged_quantity); return q !== null && q > 0; },
    chartCurrency() { return this.currency || this.prices[0]?.currency || ""; },
    chartPrices() { return this.prices.filter((p) => this.number(p.amount) !== null && (!this.chartCurrency || p.currency === this.chartCurrency)); },
    chartLabels() {
      return this.chartPrices.map((price) => this.date(price.created_at));
    },
    chartDatasets() {
      return [{
        label: this.$t('product.price_purchase'),
        data: this.chartPrices.map((price) => this.number(price.amount)),
        backgroundColor: "rgba(50, 31, 219, 0.08)",
        borderColor: "#321fdb",
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#321fdb",
        pointHoverBackgroundColor: "#321fdb",
        pointHoverBorderColor: "#ffffff",
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        lineTension: 0.2,
        fill: false,
      }];
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        hover: { mode: "index", intersect: false },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (tooltipItem) => this.money(tooltipItem.yLabel, this.chartCurrency),
          },
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { autoSkip: true, maxTicksLimit: 6, maxRotation: 0 },
          }],
          yAxes: [{
            gridLines: { color: "rgba(0, 0, 0, 0.08)", drawBorder: false },
            ticks: { padding: 8, callback: (value) => this.decimal(value) },
          }],
        },
      };
    },
  },
  methods: {
    number(value) { const n = Number.parseFloat(value); return Number.isFinite(n) ? n : null; },
    unit(value) {
      const amount = this.number(value), quantity = this.number(this.packaged_quantity);
      return amount !== null && quantity !== null && quantity > 0 ? amount / quantity : null;
    },
    money(value, currency = this.currency) {
      const amount = this.number(value);
      return amount === null ? "-" : this.$options.filters.toCurrency(amount, currency || "EUR");
    },
    date(value) { return value ? new Intl.DateTimeFormat(this.$i18n.locale || undefined).format(new Date(value)) : "-"; },
    decimal(value) { return new Intl.NumberFormat(this.$i18n.locale || undefined, { maximumFractionDigits: 2 }).format(value); },
  },
};
</script>

<style scoped>
.pph-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(145px,1fr));gap:.75rem}
.pph-stat{padding:.85rem;border:1px solid #d8dbe0;border-radius:.5rem;background:#f8f9fa}
.pph-stat small,.pph-stat span{display:block;color:#768192}.pph-stat strong{display:block;margin:.2rem 0;font-size:1.1rem}.pph-stat span{font-size:.75rem}
.pph-history-grid{display:grid;grid-template-columns:minmax(0,1.15fr) minmax(340px,.85fr);gap:1rem;align-items:stretch}
.pph-panel{display:flex;min-width:0;min-height:360px;flex-direction:column;border:1px solid #d8dbe0;border-radius:.5rem;background:#fff;overflow:hidden}
.pph-panel__header{display:flex;align-items:center;justify-content:space-between;padding:.85rem 1rem;border-bottom:1px solid #d8dbe0;background:#f8f9fa}
.pph-panel__header h6{margin:0}.pph-panel__header small{color:#768192}
.pph-chart{position:relative;flex:1;min-height:300px;padding:1rem}.pph-chart>div{height:100%}
.pph-table{max-height:318px;overflow:auto}.pph-empty{display:flex;flex:1;align-items:center;justify-content:center;flex-direction:column;gap:.5rem;color:#768192}
th{border-top:none}th,td{padding:.6rem .75rem;vertical-align:middle!important}
@media(max-width:991.98px){.pph-history-grid{grid-template-columns:minmax(0,1fr)}.pph-panel{min-height:0}.pph-chart{height:300px;flex:none}.pph-table{max-height:none}}
@media(max-width:575.98px){.pph-stats{grid-template-columns:repeat(2,minmax(0,1fr))}.pph-chart{height:250px;padding:.5rem}.pph-panel__header{padding:.75rem}}
</style>
